import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ReusableFunctions } from '../../classes/reusable-functions';
import { UserAccessService } from '../../../shared/services/user-access.service';
import { ReportingService } from 'app/modules/reports/services/reporting.service';
import { IExportColumnInfo, IRow, IRowItem } from 'app/modules/reports/interfaces/reporting-data';
import { IGLData } from 'app/modules/incentives/interfaces/incentives-data';

@Component({
    selector: 'app-financials-aag-dialog',
    templateUrl: './financials-aag-dialog.component.html',
    styleUrls: ['./financials-aag-dialog.component.scss'],
})
export class FinancialsAagDialogComponent implements OnInit {
    dialogData: Array<IGLData>;
    headerData: INetIncomeHeader;
    revenueDescriptionData: any;
    expenseDescriptionData: any;
    revenueSubgroupNames: any;
    expenseSubgroupNames: any;
    monthData: any;
    readableMonthData: any;
    dialogExpansionData: IMainDataInfo[] = [];
    math = Math;
    isDataLoading = true;
    unitsLabel = false;
    fromDatePeriod: string;
    isPriorPeriod: boolean;
    columnInfo: IExportColumnInfo[] = [];
    dataRows: IRow[] = [];
    showExportButton = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private reusableFunctions: ReusableFunctions,
        private router: Router,
        public dialogRef: MatDialogRef<FinancialsAagDialogComponent>,
        private userAccessService: UserAccessService,
        private reportingService: ReportingService
    ) {}

    ngOnInit() {
        this.dialogData = this.data.dialogData;
        this.headerData = this.data.headerData;
        this.fromDatePeriod = this.headerData.fromDatePeriod;
        this.isPriorPeriod = this.headerData.isPriorPeriod;
        this.getDialogExpansionData(this.dialogData);
        this.setGlDataInitialHorizontalLayout(this.dialogData);
        this.readableMonthData.unshift('');
        if (!this.headerData.isSingleMonth) this.readableMonthData.push('Total');
        setTimeout(() => (this.isDataLoading = false));
    }

    setRowValues(
        dataLine: IGLData[],
        dataFieldId: string,
        rowId: number,
        rowLevel: number,
        setEmptyRow: boolean,
        rowDescription: string,
        dataPointFontWeight = 400,
        fontSize = 11
    ) {
        let row: IRow = {
            rowId: rowId,
            rowData: [],
            rowOutlineLevel: rowLevel,
            outlineStart: null,
            outlineEnd: null,
        };

        let endTotal = 0;
        this.columnInfo.forEach((col) => {
            let rowItem: IRowItem = {
                columnId: col.columnId,
                dataType: col.dataType,
                value: '0',
                precision: col.precision,
                style: {
                    fontWeight: dataPointFontWeight,
                    fontSize: fontSize,
                },
                isNegativeValueBad: true,
            };
            if (col.columnIdName === 'description') {
                rowItem.value = rowDescription;
            } else if (!setEmptyRow) {
                let dataPoint = dataLine.find(
                    (line) => line.yearMonthCalendar === col.columnIdName
                );
                if (dataPoint) {
                    let value = dataPoint[dataFieldId];
                    rowItem.value = value.toString();
                    endTotal += value;
                    rowItem.valueIfNumeric = value;
                }
            }
            row.rowData.push(rowItem);
        });
        let endColumn = this.columnInfo.find((c) => c.columnIdName === 'endTotal');
        let rowEndValueInfo = row.rowData.find((r) => r.columnId === endColumn?.columnId);

        if (rowEndValueInfo) {
            rowEndValueInfo.value = endTotal.toString();
            rowEndValueInfo.valueIfNumeric = endTotal;
        }
        return row;
    }

    calculateVerticalGroupTotals(parentRow: IRow, childRows: IRow[]) {
        let childRowItems: IRowItem[] = [];
        childRows.forEach((cRow) => {
            childRowItems = childRowItems.concat(cRow.rowData);
        });

        this.columnInfo.forEach((col) => {
            if (col.columnIdName !== 'description' && col.columnName != '') {
                let columnTotal = 0;
                childRowItems.forEach((cItm) => {
                    if (cItm.columnId === col.columnId) {
                        columnTotal += cItm.valueIfNumeric ?? 0;
                    }
                });

                let parentColumn = parentRow.rowData.find((r) => r.columnId === col.columnId);
                if (parentColumn) {
                    parentColumn.valueIfNumeric = columnTotal;
                    parentColumn.value = columnTotal.toString();
                }
            }
        });
    }

    calculateVerticalDifferenceOfTwoRows(
        subtractFromRow: IRow,
        subtractingRow: IRow,
        resultingRow
    ) {
        this.columnInfo.forEach((col) => {
            if (col.columnIdName !== 'description' && col.columnName != '') {
                let subtractFromCell = subtractFromRow.rowData.find(
                    (r) => r.columnId === col.columnId
                );
                let subtractingCell = subtractingRow.rowData.find(
                    (r) => r.columnId === col.columnId
                );
                let resultingCell = resultingRow.rowData.find((r) => r.columnId === col.columnId);

                if (subtractFromCell && subtractingCell && resultingCell) {
                    let difference =
                        subtractFromCell.valueIfNumeric - subtractingCell.valueIfNumeric;
                    resultingCell.valueIfNumeric = difference;
                    resultingCell.value = difference.toString();
                }
            }
        });
    }

    setCollapsingLevels(dataClean: IGLData[]) {
        let levelOneItems = this.reusableFunctions.getUniqueItemList(dataClean, 'accountType');
        let rowId = 1;
        let levelOneProcessedRows: IRow[] = [];

        levelOneItems.forEach((levelOne) => {
            this.showExportButton =
                levelOne.toLocaleLowerCase() === 'revenue' ||
                levelOne.toLocaleLowerCase() === 'expense';

            let last2levelRowId = 0;
            let dataRows: IRow[] = [];
            let levelOneProcessedRow = this.setRowValues(
                [],
                'total',
                rowId++,
                1,
                true,
                levelOne,
                500,
                16
            );

            let levelOneData = dataClean.filter((item) => item.accountType === levelOne);
            levelOneProcessedRow.outlineStart = rowId + 1;
            let levelTwoItems = this.reusableFunctions.getUniqueItemList(
                levelOneData,
                'accountCategory'
            );
            let levelTwoRows: IRow[] = [];
            levelTwoItems.forEach((levelTwo) => {
                let levelTwoProcessedRow = this.setRowValues(
                    [],
                    'total',
                    rowId++,
                    2,
                    true,
                    levelTwo,
                    500,
                    14
                );
                last2levelRowId = levelTwoProcessedRow.rowId;
                let levelTwoData = levelOneData.filter((item) => item.accountCategory === levelTwo);
                let levelThreeItems = this.reusableFunctions.getUniqueItemList(
                    levelTwoData,
                    'accountName'
                );
                levelTwoProcessedRow.outlineStart = rowId + 1;
                levelTwoProcessedRow.outlineEnd =
                    levelTwoProcessedRow.outlineStart + levelThreeItems.length - 1;

                let levelThreeRows: IRow[] = [];
                levelThreeItems.forEach((levelThree) => {
                    let levelThreeData = levelTwoData.filter(
                        (item) => item.accountName === levelThree
                    );
                    let levelThreeProcessedRow = this.setRowValues(
                        levelThreeData,
                        'total',
                        rowId++,
                        3,
                        false,
                        levelThree,
                        400,
                        11
                    );
                    levelThreeRows.push(levelThreeProcessedRow);
                });
                this.calculateVerticalGroupTotals(levelTwoProcessedRow, levelThreeRows);
                levelTwoRows.push(levelTwoProcessedRow);
                dataRows = dataRows.concat(levelTwoProcessedRow, levelThreeRows);
            });

            let last2LevelRow = dataRows.find((r) => r.rowId === last2levelRowId);
            if (last2LevelRow) {
                last2LevelRow.outlineEnd = last2LevelRow.outlineEnd + 1;
            }
            levelOneProcessedRow.outlineEnd = rowId;
            this.calculateVerticalGroupTotals(levelOneProcessedRow, levelTwoRows);
            levelOneProcessedRows.push(levelOneProcessedRow);
            let placeHolderRowRow = this.setRowValues([], '', rowId++, 0, true, '', 200, 8);
            this.dataRows = this.dataRows.concat(levelOneProcessedRow, dataRows, placeHolderRowRow);
        });

        let lastTotalRow = this.setRowValues([], '', rowId++, 1, true, 'Total', 500, 16);
        lastTotalRow.outlineEnd = rowId;
        if (levelOneProcessedRows.length === 2) {
            this.calculateVerticalDifferenceOfTwoRows(
                levelOneProcessedRows[0],
                levelOneProcessedRows[1],
                lastTotalRow
            );
            this.dataRows.push(lastTotalRow);
        }
    }

    setGlDataInitialHorizontalLayout(data: IGLData[]) {
        let dataClean = data.filter((d) => d.levelNumber === '1' || d.level === 'AccountName');
        this.getColumnsInfo(dataClean);
        this.setCollapsingLevels(dataClean);
    }

    getColumnsInfo(data: IGLData[]) {
        let uniqueMonths = this.reusableFunctions.getUniqueItemList(data, 'yearMonthCalendar');
        uniqueMonths.sort((a, b) => (a > b ? 1 : 0));

        this.columnInfo.push({
            columnId: 1,
            columnName: '',
            columnIdName: 'description',
            dataType: 'String',
        });

        uniqueMonths.forEach((month, index) => {
            // TODO: remove complexity here
            let monthShort = this.reusableFunctions.getShortDateFormat(
                this.reusableFunctions.formatPeriodData(index, uniqueMonths)
            );
            this.columnInfo.push({
                columnId: index + 2,
                columnName: monthShort,
                columnIdName: month,
                dataType: 'Currency',
                precision: 0,
            });
        });

        if (uniqueMonths.length > 1) {
            this.columnInfo.push({
                columnId: uniqueMonths.length + 2,
                columnName: 'Total',
                columnIdName: 'endTotal',
                dataType: 'Currency',
                precision: 0,
            });
        }
    }

    getDialogExpansionData(data: IGLData[]) {
        const revenue = data.filter(
            (item) => item.levelNumber === '1' && item.accountType.toLocaleLowerCase() === 'revenue'
        );
        const level3Revenue = data.filter(
            (item) => item.levelNumber === '3' && item.accountType.toLocaleLowerCase() === 'revenue'
        );
        const expense = data.filter(
            (item) => item.levelNumber === '1' && item.accountType.toLocaleLowerCase() === 'expense'
        );
        const level3Expense = data.filter(
            (item) => item.levelNumber === '3' && item.accountType.toLocaleLowerCase() === 'expense'
        );
        const thisPeriodData = data.filter((item) => item.periodGroup === 'This Period');
        const totalRevenue = revenue.reduce((a, { total }) => a + total, 0);
        const totalExpense = expense.reduce((a, { total }) => a + total, 0);

        if (this.headerData.isVolumeFunded) {
            this.getDescriptionAndMonthData(thisPeriodData);
        } else {
            this.getDescriptionAndMonthData(data);
        }

        const totalMonthlyRevenue = level3Revenue.map((x) => x.total);
        const totalMonthlyExpense = level3Expense.map((x) => x.total);
        const totalMonthlyVolume = this.getMonthlyTotals(thisPeriodData, true, 'volume');
        const totalMonthlyUnits = this.getMonthlyTotals(thisPeriodData, true, 'units');

        if (!this.headerData.isSingleMonth) {
            totalMonthlyRevenue.push(totalRevenue);
            totalMonthlyExpense.push(totalExpense);
        }

        const revenueData: IMainDataInfo = {
            mainGroupTitle: 'Revenue',
            mainGroupAmountTotals: totalMonthlyRevenue,
            hasExpanded: true,
            subGroupData: [],
            isCurrency: true,
        };
        const expenseData: IMainDataInfo = {
            mainGroupTitle: 'Expenses',
            mainGroupAmountTotals: totalMonthlyExpense,
            hasExpanded: false,
            subGroupData: [],
            isCurrency: true,
        };

        const volumeData: IMainDataInfo = {
            mainGroupTitle: 'Volume',
            mainGroupAmountTotals: totalMonthlyVolume,
            hasExpanded: true,
            subGroupData: [],
            isCurrency: true,
            isVolumeFunded: true,
        };

        const unitData: IMainDataInfo = {
            mainGroupTitle: 'Units',
            mainGroupAmountTotals: totalMonthlyUnits,
            hasExpanded: true,
            subGroupData: [],
            isCurrency: false,
            isVolumeFunded: true,
        };

        this.addDataToDialogExpansion(revenueData, expenseData, volumeData, unitData);
    }

    getDescriptionAndMonthData(data: IGLData[]) {
        const monthArr = [];
        const abbrMonthArr = [];
        const revenueDescriptions = [];
        const expenseDescriptions = [];
        const revenueSubNames = [];
        const expenseSubNames = [];

        data.forEach((item) => {
            if (
                item.accountType?.toLocaleLowerCase() === 'revenue' &&
                item.levelNumber === '1' &&
                revenueDescriptions.findIndex((x) => x.name === item.accountName) < 0
            ) {
                revenueDescriptions.push({
                    name: item.accountName,
                    category: item.accountCategory,
                });
            } else if (
                item.accountType?.toLocaleLowerCase() === 'expense' &&
                item.levelNumber === '1' &&
                expenseDescriptions.findIndex((x) => x.name === item.accountName) < 0
            ) {
                expenseDescriptions.push({
                    name: item.accountName,
                    category: item.accountCategory,
                });
            }

            if (!monthArr.includes(item.yearMonthCalendar)) {
                monthArr.push(item.yearMonthCalendar);
            }
        });

        monthArr.forEach((month, i) => {
            const date = this.reusableFunctions.getShortDateFormat(
                this.reusableFunctions.formatPeriodData(i, monthArr)
            );
            abbrMonthArr.push(date);
        });

        revenueDescriptions.forEach((item) => {
            if (!revenueSubNames.includes(item.category)) {
                revenueSubNames.push(item.category);
            }
        });

        expenseDescriptions.forEach((item) => {
            if (!expenseSubNames.includes(item.category)) {
                expenseSubNames.push(item.category);
            }
        });

        this.revenueDescriptionData = revenueDescriptions;
        this.expenseDescriptionData = expenseDescriptions;
        this.revenueSubgroupNames = revenueSubNames.map((x) => this.toTitleCase(x));
        this.expenseSubgroupNames = expenseSubNames.map((x) => this.toTitleCase(x));
        this.monthData = monthArr;
        this.readableMonthData = abbrMonthArr;
    }

    getMonthlyTotals(data: IGLData[], isVolumeFunded: boolean = false, measure?: string) {
        const monthlyData: number[] = [];
        for (let month of this.monthData) {
            let monthData;
            if (!isVolumeFunded) {
                monthData = data
                    .filter((item) => item.yearMonthCalendar === month)
                    .reduce((a, { total }) => a + total, 0);
            } else {
                if (measure === 'volume')
                    monthData = data.find((item) => item.yearMonthCalendar === month)?.volume;
                else monthData = data.find((item) => item.yearMonthCalendar === month)?.units;
            }

            monthlyData.push(monthData);
        }

        if (isVolumeFunded) {
            const sumVolume = monthlyData.reduce((a, b) => a + b, 0);
            monthlyData.push(sumVolume);
        }

        return monthlyData;
    }

    getSubgroupData(dialogData, isRevenue: boolean) {
        const subgroupNames = isRevenue ? this.revenueSubgroupNames : this.expenseSubgroupNames;
        const revenueExpenseVerbiage = isRevenue ? 'Revenue' : 'Expense';
        const subgroup = [];
        subgroupNames.forEach((name) => {
            const sub = {
                subGroupTitle: this.toTitleCase(name),
                totals: [],
                hasExpanded: false,
                individualData: [],
            };
            subgroup.push(sub);
        });
        const descriptionData = isRevenue
            ? this.revenueDescriptionData
            : this.expenseDescriptionData;

        // Make an initial individualized data under subgroup. ex. Fees - Credit Report, etc.
        descriptionData.forEach((item) => {
            const category = this.toTitleCase(item.category);
            const index = subgroupNames.indexOf(category);
            if (index >= 0) {
                subgroup[index].individualData.push({
                    title: item.name,
                    data: [],
                    total: 0,
                    tempData: [],
                });
            }
        });

        // Fill subgroups with individual items' Monthly data ex. May'22 Fees - Credit Report, June'22 ...
        this.dialogData.forEach((item) => {
            const category = this.toTitleCase(item.accountCategory);
            const outerIndex = subgroupNames.indexOf(category);
            const innerIndex =
                outerIndex >= 0
                    ? subgroup[outerIndex].individualData.findIndex(
                          (x) => x.title === item.accountName
                      )
                    : -1;
            if (
                innerIndex >= 0 &&
                item.levelNumber === '1' &&
                this.toTitleCase(item.accountType) === revenueExpenseVerbiage
            ) {
                subgroup[outerIndex].individualData[innerIndex].tempData.push(item);
            }
        });
        // Get Subgroup totals & fill in months that don't have data
        subgroup.forEach((item) => {
            const tempTotals = new Array(this.monthData.length).fill(0);
            const tempObjTotals = new Array(this.monthData.length).fill({ total: 0 });

            item.individualData.forEach((i, index) => {
                i.data = JSON.parse(JSON.stringify(tempObjTotals));
                i.tempData.forEach((t) => {
                    const monthIndex = this.monthData.indexOf(t.yearMonthCalendar);
                    i.data[monthIndex] = t;
                });

                const totals = i.data.reduce((a, { total }) => a + total, 0);
                i.total = totals;
                i.data.forEach((j, index) => {
                    tempTotals[index] += j.total;
                });
            });
            item.totals = JSON.parse(JSON.stringify(tempTotals));
            const subgroupRowTotal = item.totals.reduce((a, b) => a + b, 0);
            if (!this.headerData.isSingleMonth) item.totals.push(subgroupRowTotal);
        });

        return subgroup;
    }

    addDataToDialogExpansion(
        revenueData: IMainDataInfo,
        expenseData: IMainDataInfo,
        volumeData: IMainDataInfo,
        unitData: IMainDataInfo
    ) {
        switch (this.headerData.title) {
            case 'Loan Revenue':
            case 'Non-Loan Revenue':
            case 'LOAN REVENUE':
            case 'TOTAL REVENUE':
            case 'Total Revenue':
                this.dialogExpansionData.push(revenueData);
                this.dialogExpansionData[0].subGroupData = this.getSubgroupData(
                    this.dialogExpansionData,
                    true
                );
                break;
            case 'Loan Expense':
            case 'Non-Loan Expenses':
            case 'LOAN EXPENSES':
            case 'TOTAL EXPENSES':
            case 'Total Expenses':
            case 'Total Expense':
                expenseData.hasExpanded = true;
                this.dialogExpansionData.push(expenseData);
                this.dialogExpansionData[0].subGroupData = this.getSubgroupData(
                    this.dialogExpansionData,
                    false
                );
                break;
            case 'Loan Net Income':
            case 'Non-Loan Net Income':
            case 'LOAN NET INCOME':
            case 'TOTAL NET INCOME':
            case 'Total Net Income':
                revenueData.hasExpanded = false;
                this.dialogExpansionData.push(revenueData, expenseData);
                this.dialogExpansionData[0].subGroupData = this.getSubgroupData(
                    this.dialogExpansionData,
                    true
                );
                this.dialogExpansionData[1].subGroupData = this.getSubgroupData(
                    this.dialogExpansionData,
                    false
                );
                break;
            case 'VOLUME FUNDED':
                this.dialogExpansionData.push(volumeData, unitData);
                this.unitsLabel = true;
        }
    }

    navigateToDrilldown(
        level: number,
        accountName: string,
        ymc: any,
        title: string,
        isTotal?: boolean,
        datePeriod?: string,
        isRevenue?: string,
        accountCategory?: string
    ) {
        const reportUrl = '/reporting/financials/revenue-expenses';
        const hasAccessToReports = this.userAccessService.hasAccessToPage(reportUrl);
        if (!hasAccessToReports) return;
        if (this.headerData.isVolumeFunded) {
            return this.navigateToVolumeDrilldown(ymc);
        }
        let loanAccount = 2;
        let fromDatePeriod;

        const lowerTitle = this.headerData.title.toLowerCase();
        if (lowerTitle.includes('loan') && !lowerTitle.includes('non')) {
            loanAccount = 1;
        } else if (lowerTitle.includes('non')) {
            loanAccount = 0;
        }
        const yearMonthCalendar = ymc ? ymc : null;
        if (level === 3) {
            accountName = accountName.replace('Expenses', 'Expense');
        }

        if (!isTotal) {
            const thisYear = new Date().getFullYear();
            const lastYear = thisYear - 1;
            const ymcYear = Number(yearMonthCalendar.split('-')[0]);
            if (ymcYear === thisYear) {
                fromDatePeriod = 'This Year';
            } else if (ymcYear === lastYear) {
                fromDatePeriod = 'Last Year';
            } else {
                fromDatePeriod = 'Last Year Prior';
            }
        }
        const acctName = level >= 2 ? 'ALL' : accountName;
        const acctType = level > 2 ? accountName : isRevenue.replace('Expenses', 'Expense');
        const acctCategory = level > 2 ? 'ALL' : level > 1 ? accountName : accountCategory;
        const queryParams = {
            dateRange_y_q_m: isTotal ? datePeriod : fromDatePeriod,
            ymc: yearMonthCalendar,
            accountName: acctName,
            accountType: acctType,
            accountCategory: acctCategory,
            titleSuffix: title,
            loanAccount,
            dateRange: isTotal
                ? this.headerData.displayDates
                : this.getHardDisplayOptionDate(yearMonthCalendar),
        };

        this.dialogRef.close();
        this.router.navigate([reportUrl], {
            queryParams,
        });
    }

    navigateToVolumeDrilldown(ymc: string) {
        const ymcArr = ymc.split('-');
        const year = ymcArr[0];
        const month = ymcArr[1];
        this.dialogRef.close();
        this.router.navigate(['/reporting/financials/volume-funded'], {
            queryParams: {
                viewBy_m_w: 'Month',
                loanPurpose: 'All',
                loanCategory: 'All',
                year,
                milestonekey: '22',
                channel: 'All',
                selectedPeriod: month,
                titleSuffix: 'Volume Funded',
                dateRange: this.getHardDisplayOptionDate(ymc),
            },
        });
    }

    toTitleCase(str) {
        return str?.replace(
            /\w\S*/g,
            (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
    }

    getPageTitle(mainTitle: string, subTitle: string) {
        // Subtitle can be undefined in level 3 drilldowns (ex. Revenues, no subtitles). For drilldowns without account names, it comes back as null.
        const isSubTitleEmpty = subTitle === '' ? true : false;
        if (isSubTitleEmpty) {
            return mainTitle;
        } else {
            if (subTitle === null) {
                return `${mainTitle}: Unknown`;
            } else {
                return `${mainTitle}: ${subTitle}`;
            }
        }
    }

    getHardDisplayOptionDate(yearMonthCalendar: string) {
        const year = Number(yearMonthCalendar.split('-')[0]);
        const abbrYear = year.toString().slice(2);
        const month = Number(yearMonthCalendar.split('-')[1]);
        const thirtyDays = [4, 6, 9, 11];
        let day;
        if (thirtyDays.includes(month)) {
            day = 30;
        } else if (month === 2) {
            if (new Date(year, 1, 29).getDate() === 29) {
                day = 29;
            } else {
                day = 28;
            }
        } else {
            day = 31;
        }
        return `${month}/01/${year} - ${month}/${day}/${year}`;
    }

    exportToExcel() {
        let exportName = 'Total-Net-Income-';
        let exportData = [];
        this.reportingService.setQuickExportData(
            this.columnInfo,
            exportData,
            exportName,
            true,
            this.dataRows
        );
    }
}

interface IMainDataInfo {
    mainGroupTitle: string;
    mainGroupAmountTotals: Array<number>;
    hasExpanded: boolean;
    subGroupData: Array<any>;
    isCurrency: boolean;
    isVolumeFunded?: boolean;
}

interface INetIncomeHeader {
    fromDatePeriod: string;
    isPriorPeriod: boolean;
    isVolumeFunded: boolean;
    isSingleMonth: boolean;
    title: string;
    displayDates: string;
    value: number;
    bps: number;
}
